import { faPlusCircle, faTrash, faChevronDown,faX, faImage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useRef, useEffect } from 'react';
import { decryptData } from '../../utils/storageCrypto'; // Adjust path as needed
import { Msg200or201, Msg204 } from '../Messages/Messages';


// Custom hook to handle outside clicks
const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);
};

// Custom Dropdown Component (same as UOM dropdown)
const CustomDropdown = ({ options, selected, onSelect, showAddNew, placeholder = "Select Brand" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  

  const filteredOptions = options.filter(option => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOptionClick = (value, label, id) => {
    onSelect(value, label, id);
    setIsOpen(false);
    setFocusedOptionIndex(null);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) { 
          setIsOpen(true);
        }
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) { 
          setIsOpen(true);
        }
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedOptionIndex !== null) {
          const selectedOption = filteredOptions[focusedOptionIndex];
          handleOptionClick(selectedOption.value, selectedOption.label, selectedOption.id);
        }
        break;
      default:
        break;
    }
  };

  const scrollToOption = (index) => {
    const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
    if (optionElements && optionElements[index]) {
      optionElements[index].scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  useOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-sm ${selected ? 'text-black' : 'text-[#838195]'}`}>
          {selected || placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-500" />
      </div>
      {isOpen && (
        <div className="absolute mt-1 w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-md z-10 flex flex-col">
          <input
            type="text"
            className="p-1 border rounded-md border-blue-500 focus:outline-none hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus={isOpen}
            ref={searchInputRef}
          />
          <div className="overflow-y-auto scrollbar-visible flex-grow">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  className={`p-2 cursor-pointer text-sm ${index === focusedOptionIndex ? 'bg-blue-500 text-white' : ''} hover:bg-blue-500 hover:text-white`}
                  onClick={() => handleOptionClick(option.value, option.label, option.id)}
                >
                  {option.icon && <FontAwesomeIcon icon={option.icon} className="mr-2" />}
                  {option.label}
                </div>
              ))
            ) : (
              <div className="p-2 text-sm text-gray-500">
                No brands available
              </div>
            )}
          </div>
          {showAddNew && (
            <button
              type="button"
              className="flex items-center  text-sm border-t p-2 mt-2 text-blue-500 hover:bg-blue-50"
              onClick={showAddNew}
            >
              <FontAwesomeIcon icon={faPlusCircle} className="w-4 h-4 mr-2" />
              New Brand
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// New Brand Popup Component
const NewBrandPopup = ({ isOpen, onClose, onSave, isSaving, errorMessages, setErrorMessages  }) => {
  const [brandName, setBrandName] = useState('');
  const [brandVideo, setBrandVideo] = useState('');
  const [brandLogo, setBrandLogo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
   

 const handleSubmit = () => {
    const errors = [];
    if (!brandName.trim()) {
      errors.push('Brand name is required');
    }
    // if (!brandLogo) {
    //   errors.push('Logo is required');
    // }
    if (errors.length > 0) {
      setErrorMessages(errors);
      return;
    }
    
    // ✅ FormData EXACTLY matches backend
    const formData = new FormData();
    formData.append('name', brandName.trim());     // ✅ Backend expects 'name'
    formData.append('video', brandVideo || '');    // ✅ Backend expects 'video'
    formData.append('image', brandLogo);           // ✅ Backend expects 'logo' NOT 'image'
    
    console.log('📤 Sending FormData:', {
      name: brandName.trim(),
      hasImage: !!brandLogo,
      video: brandVideo || 'empty',
    });
    
    onSave(formData);
    
    // Reset form
    setBrandName('');
    setBrandVideo('');
    setBrandLogo(null);
    setImagePreview(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBrandLogo(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-top justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white rounded-b-md shadow-md w-1/2 h-2/3 flex flex-col animate-slide-in">
        <div className='bg-[#F9F9FB] flex justify-between items-center w-full h-[10%] px-6'>
          <h2 className="text-md font-semibold">New Brand</h2>
          <button onClick={onClose} className="text-[#e54643] hover:text-red-600 px-1 py-1 rounded-md">
            <FontAwesomeIcon icon={faX} className="text-xs cursor-pointer" />
          </button>
        </div>
        <hr className="border-t border-gray-200 w-full" style={{ borderTopWidth: '1px' }} />
        
        {errorMessages.length > 0 && (
          <div className='p-1'>
            <div className="mt-1 flex justify-between items-center px-auto py-1 bg-[#fddcde] rounded-md">
              <div className="px-6 flex flex-col justify-start items-start">
                {errorMessages.map((error, index) => (
                  <span key={index} className="text-sm">• {error}</span>
                ))}
              </div>
              <button
                className="text-[#F7525A] hover:text-red-600 px-4"
                onClick={() => setErrorMessages([])}
              >
                <FontAwesomeIcon icon={faX} className="text-xs cursor-pointer" />
              </button>
            </div>
          </div>
        )}

       <div className="flex flex-col p-6 flex-grow overflow-y-auto">
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
    
    {/* LEFT SIDE: Brand Name + Video URL - YOUR WIDTH w-64 */}
    <div className="space-y-6">
      {/* Brand Name */}
      <div className="w-full flex flex-row items-center mb-6 space-x-4 mt-4">
        <label className="block text-sm font-semibold text-[#e54643] w-24 flex-shrink-0">
          Brand Name<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-64 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          placeholder="Enter brand name"
        />
      </div>

      {/* Video URL - YOUR SAME WIDTH w-64 */}
      <div className="w-full flex flex-row items-center mb-6 space-x-4">
        <label className="block text-sm font-semibold text-gray-700 w-24 flex-shrink-0">
          Video URL
        </label>
        <input
          type="url"
          className="w-64 outline-none block rounded-md border border-gray-300 hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 p-2 text-sm text-gray-700"
          value={brandVideo}
          onChange={(e) => setBrandVideo(e.target.value)}
          placeholder="https://example.com/brand-video.mp4"
        />
      </div>
    </div>

    {/* RIGHT SIDE: Logo Upload */}
    <div className="flex justify-end items-start mr-4">
      <div className="w-38 h-28 border-2 border-dashed border-gray-300 rounded-lg flex justify-center items-center relative cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200">
        <input
          type="file"
          accept="image/*"
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          onChange={handleLogoChange}
        />
        {imagePreview ? (
          <img 
            src={imagePreview} 
            alt="Logo Preview" 
            className="w-full h-full object-cover rounded-lg shadow-md" 
          />
        ) : (
          <div className="flex flex-col justify-center items-center text-gray-400 text-center text-xs w-full h-full p-4">
            <FontAwesomeIcon icon={faImage} className="w-8 h-8 mx-auto mb-3 text-gray-400" />
            <div className="space-y-1">
              <span className="text-xs">Click to upload</span>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
</div>

        <div className="mt-auto w-full p-4 border-t border-gray-200">
          <div className="flex justify-start items-center space-x-2">
            <button 
              className="px-4 py-1 bg-[#f7525a] border border-[#f7525a] text-white rounded-md flex items-center space-x-2 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              <span>Save</span>
              {isSaving && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
            </button>
            <button 
              className="px-4 py-1 bg-[#f5f5f5] text-gray-700 border border-[#DDDDDD] rounded-md hover:bg-gray-100 disabled:opacity-50"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Brands({
  brands = [],
  onChange,
  addBrand,
  removeBrand,
  fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET, // Add this prop or get from context
}) {
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [showNewBrandPopup, setShowNewBrandPopup] = useState(false);
  const [isSavingBrand, setIsSavingBrand] = useState(false);
  const [brandErrorMessages, setBrandErrorMessages] = useState([]);
  const [selectedBrandForRow, setSelectedBrandForRow] = useState({});
    const [showPopup, setShowPopup] = useState(false);
        const [popupMessage, setPopupMessage] = useState('');
        const [popupType, setPopupType] = useState('');

  // Fetch brands (similar to getUoms)
  

 const handleBrandSelect = (value, label, id, rowIndex) => {

  const updatedData = [...brands];

  updatedData[rowIndex].name = label;
  updatedData[rowIndex].brandId = id;
  updatedData[rowIndex].brandImage = null;

  // Find selected brand option
  const selectedBrandOption = brandsOptions.find(option => option.id === id);
  console.log("🔍 Selected Brand Option:", selectedBrandOption);

  if (selectedBrandOption) {
    updatedData[rowIndex].selectedBrandImage = selectedBrandOption.brandImageUrl;
    updatedData[rowIndex].video = selectedBrandOption.brandVideo || '';

    console.log("🎯 After Setting Image & Video:", {
      selectedBrandImage: updatedData[rowIndex].selectedBrandImage,
      video: updatedData[rowIndex].video
    });
  } else {
    console.log("⚠️ No matching brand option found for id:", id);
  }
  onChange(updatedData);
  setSelectedBrandForRow(prev => {
    const updated = { ...prev, [rowIndex]: label };
    console.log("🧠 Updated Selected Brand Row State:", updated);
    return updated;
  });
};

  const handleInputChange = (e, index, field) => {
    const updatedData = [...brands];
    updatedData[index][field] = e.target.value;
    onChange(updatedData);
  };

  const handleImageChange = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const updatedData = [...brands];
      updatedData[index].image = file;
      onChange(updatedData);
    }
  };

  // Drag and drop handlers (keeping existing)
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.parentElement.classList.add('border-blue-500', 'bg-blue-50');
  };

  const handleDragLeave = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.parentElement.classList.remove('border-blue-500', 'bg-blue-50');
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.parentElement.classList.remove('border-blue-500', 'bg-blue-50');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const updatedData = [...brands];
        updatedData[index].image = file;
        onChange(updatedData);
      } else {
        alert('Please select an image file');
      }
    }
  };

  // Add this function inside the Brands component, before the return statement
 const handleNewBrandSave = async (formData) => {
  setIsSavingBrand(true);
  setBrandErrorMessages([]);

  try {
    // 🔐 Get token
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    
formData.append('organizationIds', JSON.stringify([]));
    // 🚀 API CALL
    const response = await fetch(`${fetchUrl}vendor/create-brand`, {
      method: 'POST',
      headers: {
        'x-token': token,   // ❗ DO NOT add Content-Type for FormData
      },
      body: formData,
    });

    const data = await response.json();
    console.log("✅ Create Brand Response:", data);

    // ✅ SUCCESS CHECK (IMPORTANT)
    if (response.ok) {

      // 🔄 Refresh dropdown brands
      const updatedBrands = await fetchBrandsOptions();
      setBrandsOptions(updatedBrands);

      // ❌ Close popup
      setShowNewBrandPopup(false);
      setBrandErrorMessages([]);

      // ✅ Success popup
      setPopupType('200or201');
      setPopupMessage('Brand created successfully!');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);

    } else {
      // ❌ Backend error
      setBrandErrorMessages([data.message || 'Failed to create brand']);
    }

  } catch (error) {
    console.error("❌ API Error:", error);
    setBrandErrorMessages(['Network error. Please try again.']);
  } finally {
    setIsSavingBrand(false);
  }
};
 

// Add this function to fetch brands options
const fetchBrandsOptions = async () => {
  try {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    
    const response = await fetch(`${fetchUrl}vendor/get-brands`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
    });

    const responseData = await response.json();
    console.log('Fetched brands:', responseData); 
    
    if (responseData.status === 200) {
      return responseData.data.map(brand => {
        // Handle "null" string and construct image URL
        const imageUrl = brand.image && brand.image !== "null" 
          ? `${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${brand.image}`
          : null;
          
        return {
          value: brand._id,
          label: brand.name,
          id: brand._id,
          brandImageUrl: imageUrl ,// Store the full image URL
          brandVideo: brand.video || ''
        };
      });
    }
    return [];
  } catch (error) {
    console.error('Error fetching brands:', error);
    return [];
  }
};

 useEffect(() => {
    fetchBrandsOptions().then(setBrandsOptions);
  }, []);

  return (
    <>
      <div className="w-full mt-5">
        <div className="w-full">
          <div className="w-full flex flex-col justify-start items-center">
            <div className="w-full flex flex-col justify-start items-start">
              <div className="w-full container mx-auto px-4 mb-2">
                <table className="w-full">
                  <thead>
                    <tr className="text-[#6c7184] bg-[#fafafc]">
                      <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">Logo</th>
                      <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">Name</th>
                      <th className="font-semibold text-left text-sm py-2 px-2 border cursor-pointer border">Video</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(brands) ? brands : []).map((brand, index) => (
                      <tr key={index}>
                        <td className="border-t border-r border-b border-gray-200 p-2 text-center">
                          <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex justify-center items-center relative mx-auto">
                            <input
                              type="file"
                              name="image"
                              accept="image/*"
                              id={`file-input-${index}`}
                              onChange={(e) => handleImageChange(index, e)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={(e) => handleDragLeave(e, index)}
                              onDrop={(e) => handleDrop(e, index)}
                              className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <label htmlFor={`file-input-${index}`} className="cursor-pointer flex justify-center items-center w-full h-full">
                      {brand.image || brand.selectedBrandImage ? (
  <img 
    src={
      brand.image instanceof File
        ? URL.createObjectURL(brand.image)  // New upload
        : brand.selectedBrandImage          // Brand dropdown image
    }
    alt="Brand Logo" 
    className="w-16 h-16 object-cover rounded-lg shadow-md" 
    onError={(e) => {
      console.log("❌ Image failed:", e.target.src);
      e.target.style.display = 'none';  // Hide broken images
    }}
  />
) : (
  // 🔥 SHOW PLACEHOLDER if no image
  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
    <FontAwesomeIcon icon={faImage} className="w-8 h-8 text-gray-400" />
  </div>
)}
                               
                            </label>
                          </div>
                        </td>
                        <td className="text-xs border p-2">
                          <CustomDropdown
                            options={brandsOptions}
                            selected={selectedBrandForRow[index] || brand.name}
                            onSelect={(value, label, id) => handleBrandSelect(value, label, id, index)}
                            showAddNew={() => setShowNewBrandPopup(true)}
                            placeholder="Select Brand"
                          />
                        </td>
                        <td className="text-xs border">
                          <input
                            type="text"
                            value={brand.video || ''}
                            onChange={(e) => handleInputChange(e, index, 'video')}
                            className="outline-blue-500 text-sm w-full h-full p-2 bg-transparent rounded-md hover:border-blue-500 hover:ring-1 hover:ring-blue-500 hover:ring-opacity-90"
                            placeholder="Enter video URL"
                          />
                        </td>
                        <td className="px-2 text-center">
                          <button
                            onClick={() => removeBrand(index)}
                            className="text-red-500 hover:text-red-700 focus:outline-none"
                          >
                            <FontAwesomeIcon className="w-3 h-3" icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                          <div>
                <button
                  onClick={addBrand}
                  className="text-blue-500 text-sm py-2 px-4 hover:text-blue-600"
                >
                  <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                  Add Brand
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Brand Popup */}
      <NewBrandPopup
        isOpen={showNewBrandPopup}
        onClose={() => {
          setShowNewBrandPopup(false);
          setBrandErrorMessages([]);
        }}
        onSave={handleNewBrandSave}
        isSaving={isSavingBrand}
        errorMessages={brandErrorMessages}
        setErrorMessages={setBrandErrorMessages}
      />

      {showPopup && (
        <div>
          {popupType === '200or201' && <Msg200or201 message={popupMessage} timeout={3000} />}
          {popupType === '204' && <Msg204 message={popupMessage} timeout={3000} />}
         </div>
      )}
      
    </>
  );
}

export default Brands;